import React, { useRef, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export const ProfitLossReport = ({ combinedData, schoolData, totalsByKhate, onClose,fyear }) => {
  const taluka = schoolData ? schoolData.taluka : "";
  const jilha = schoolData ? schoolData.jilha : "";
  const printRef = useRef();
  const [language, setLanguage] = useState(localStorage.getItem("language") || "English");
  const [attachedCredit, setAttachedCredit] = useState("");
  const [studentFeeTotal, setStudentFeeTotal] = useState(0);
  const udiseNumber = localStorage.getItem("udiseNumber");

  // Get kird with fee attach data from IndexedDB
  const getKirdWithFeeattachData = () => {
    return new Promise((resolve, reject) => {
      if (!fyear) {
        reject(new Error("Financial year not found"));
        return;
      }

      const openRequest = indexedDB.open('SchoolManagementDB', 1);
      
      openRequest.onerror = (event) => {
        console.error("Database error:", event.target.error);
        reject(event.target.error);
      };

      openRequest.onsuccess = (event) => {
        const db = event.target.result;
        try {
          const transaction = db.transaction(['kirdWithFeeattach'], 'readonly');
          const objectStore = transaction.objectStore('kirdWithFeeattach');
          
          // Verify the key exists before making the request
          const keyRange = IDBKeyRange.only(fyear);
          const getRequest = objectStore.get(keyRange);
          
          getRequest.onerror = (event) => {
            console.error("Object store error:", event.target.error);
            reject(event.target.error);
          };
          
          getRequest.onsuccess = (event) => {
            resolve(event.target.result);
          };
        } catch (error) {
          reject(error);
        }
      };

      openRequest.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('kirdWithFeeattach')) {
          db.createObjectStore('kirdWithFeeattach', { keyPath: 'fyear' });
        }
      };
    });
  };

  // Fetch combined student fees
  const fetchCombinedStudentFees = async () => {
    try {
      if (!udiseNumber || !fyear) {
        console.warn("Missing UDISE number or financial year");
        return;
      }

      const kirdData = await getKirdWithFeeattachData();
      if (kirdData && kirdData.typeofattach === "combinedPavati") {
        setAttachedCredit(kirdData.creditKhate || "");
        
        // Fetch student fees from Firebase
        const studentFeesUrl = `${process.env.REACT_APP_FIREBASE_DATABASE_URL}/schoolRegister/${udiseNumber}/studentFee/${fyear}.json`;
        const response = await fetch(studentFeesUrl);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch student fees: ${response.status}`);
        }
        
        const studentFees = await response.json();
        
        let totalStudentFees = 0;
        const lastBeforePayMap = {};

        if (studentFees) {
          for (const serialNo in studentFees) {
            for (const invoiceNo in studentFees[serialNo]) {
              const feeData = studentFees[serialNo][invoiceNo];
              
              for (const feeType in feeData) {
                const feeDetails = feeData[feeType];
                const previousTotal = lastBeforePayMap[`${serialNo}_${feeType}`] || 0;
                const currentBeforePay = parseFloat(feeDetails.beforePay) || 0;
                const paidAmount = currentBeforePay - previousTotal;

                if (paidAmount > 0) {
                  totalStudentFees += paidAmount;
                  lastBeforePayMap[`${serialNo}_${feeType}`] = currentBeforePay;
                }
              }
            }
          }
        }

        setStudentFeeTotal(totalStudentFees);
      }
    } catch (error) {
      console.error("Error in fetchCombinedStudentFees:", error);
    }
  };

  useEffect(() => {
    fetchCombinedStudentFees();
  }, []);

  const handlePrint = () => {
    const printWindow = window.open('', '', 'height=600,width=1000');
    printWindow.document.write('<html><head><title>Profit-Loss Report</title>');
    printWindow.document.write(`
      <style>
        body {
          font-family: Arial, sans-serif; 
          margin: 20px; 
        }
        table {
          width: 100%;
          max-width: 700px;
          border-collapse: collapse;
          margin: 10px auto;
        }
        th, td {
          border: 1px solid #000;
          padding: 8px;
          text-align: center;
        }
        th {
          background-color: #007bff;
          color: white;
        }
        tr:nth-child(even) {
          background-color: #f2f2f2;
        }
        h1, h3 {
          text-align: center;
        }
        .school-info {
          background-color: #f0f0f0;
          padding: 20px;
          margin-bottom: 20px;
          text-align: center;
          border: 1px solid #000;
        }
        .header-title {
          background-color: #000;
          color: #fff;
          padding: 5px;
          display: block; 
          width: fit-content; 
          margin: 10px auto;
          border-radius: 10px;
        }
        .student-fee-row {
          background-color: #e8f5e9;
        }
        @media print {
          body {
            margin: 0;
          }
          table {
            width: 680px !important;
            margin: auto;
          }
          ::-webkit-scrollbar {
            display: none;
          }
          body {
            overflow: hidden;
          }
        }
      </style>
    `);
    printWindow.document.write('</head><body>');
    printWindow.document.write(printRef.current.innerHTML);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };

  // Prepare data for the table
  const tableData = [];
  let totalCredit = 0;
  let totalDebit = 0;

  Object.keys(totalsByKhate).forEach(khateName => {
    if (khateName.trim().toLowerCase() === "31-03-akher shillak".toLowerCase()) {
      return; // Skip this iteration
    }

    const { credit, debit } = totalsByKhate[khateName] || { credit: 0, debit: 0 };

    // Add row data to the table
    tableData.push([khateName, debit || "", khateName, credit || ""]);

    // Add to grand total
    totalCredit += credit || 0;
    totalDebit += debit || 0;
  });

  // Add student fee to total credit if attached
  if (attachedCredit && studentFeeTotal > 0) {
    totalCredit += studentFeeTotal;
  }

  const profit = totalCredit - totalDebit;
  const profitValue = profit > 0 ? profit : 0;
  const lossValue = profit < 0 ? Math.abs(profit) : 0;

  tableData.push(["Profit", profitValue, "Loss", lossValue]);

  // Add total row to the table
  tableData.push(["Total", totalDebit + profitValue, "Total", totalCredit + lossValue]);

  return (
    <div style={report2Overlay}>
      <div style={report2Container}>
        <button style={closeButtonStyle2} onClick={onClose}>Close</button>
        <button
          onClick={handlePrint}
          style={{
            margin: '10px 0',
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
          }}
        >
          Print Report
        </button>
        
        <div ref={printRef}>
          <div
            style={{
              maxWidth: '800px',
              margin: '0 auto',
              padding: '20px',
              fontFamily: 'Arial, sans-serif',
              border: '1px solid #ccc',
              borderRadius: '8px',
              boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
              border: '1px solid #000',
              overflowY: "auto",
              maxHeight: "70vh"
            }}
          >
            {/* School Info Section */}
            <div
              style={{
                backgroundColor: '#f0f0f0',
                padding: '20px',
                marginBottom: '20px',
                textAlign: 'center',
                border: '1px solid #000',
              }}
            >
              <h1
                style={{
                  margin: '0',
                  fontSize: '24px',
                  fontWeight: 'bold',
                }}
              >
                {schoolData.schoolName}
              </h1>
              <p
                style={{
                  margin: '5px 0',
                  fontSize: '16px',
                }}
              >
                {language === "English" ? "Taluka: " : "तालुका: "} {taluka},
                {language === "English" ? " District: " : " जिल्हा: "} {jilha}
              </p>
              <p
                style={{
                  margin: '5px 0',
                  fontSize: '16px',
                }}
              >
                {language === "English" ? "Email: " : "ई-मेल: "} {schoolData.email}, {language === "English" ? "Mobile Number: " : "मोबाईल नंबर: "} {schoolData.mobileNumber}
              </p>
            </div>

            <h3
              style={{
                textAlign: 'center',
                backgroundColor: '#000',
                color: '#fff',
                padding: '5px',
                display: 'block',
                width: 'fit-content',
                margin: '0 auto',
                borderRadius: '10px',
                marginBottom: '10px',
                fontSize: '20px',
              }}
            >
              {language === "English" ? " Profit-Loss Report " : "नफा-तोटा पत्र "} 
            </h3>

            <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <table
                style={{
                  width: '700px',
                  borderCollapse: 'collapse',
                  marginTop: '10px',
                  margin: 'auto',
                }}
              >
                <thead>
                  <tr>
                    <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', backgroundColor: '#007bff', color: 'white' }}>
                      <h5> {language === "English" ? " Expenses " : "खर्च"} </h5>
                    </th>
                    <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', backgroundColor: '#007bff', color: 'white' }}>
                      <h5>{language === "English" ? " Amount " : "रक्कम"}</h5>
                    </th>
                    <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', backgroundColor: '#007bff', color: 'white' }}>
                      <h5>{language === "English" ? " Income " : "उत्पन्न"}</h5>
                    </th>
                    <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', backgroundColor: '#007bff', color: 'white' }}>
                      <h5>{language === "English" ? " Amount " : "रक्कम"}</h5>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Student Fee Row - Only show if connected */}
                  {attachedCredit && studentFeeTotal > 0 && (
                    <tr className="student-fee-row">
                      <td style={{ border: "1px solid #000", textAlign: 'center'}} colSpan="2"></td>
                      <td style={{ border: "1px solid #000", textAlign: 'center'}}>
                        {attachedCredit} <br />
                        <small style={{color: '#836459'}}>
                          {language === "English" ? "Student Fees" : "विद्यार्थी फी"}
                        </small>
                      </td>
                      <td style={{ border: "1px solid #000", textAlign: 'center'}}>{studentFeeTotal.toFixed(2)}</td>
                    </tr>
                  )}

                  {Object.keys(totalsByKhate).map((khateName, index) => {
                    const { credit, debit } = totalsByKhate[khateName] || { credit: 0, debit: 0 };
                    if (khateName.trim().toLowerCase() === '31-03-akher shillak') {
                      return null;
                    }
                    return (
                      <tr key={khateName} style={{ backgroundColor: index % 2 === 0 ? '#f2f2f2' : '' }}>
                        <td style={{ border: "1px solid #000", textAlign: 'center'}}>{khateName}</td>
                        <td style={{ border: "1px solid #000", textAlign: 'center'}}>{debit || ''}</td>
                        <td style={{ border: "1px solid #000", textAlign: 'center'}}>{khateName}</td>
                        <td style={{ border: "1px solid #000", textAlign: 'center'}}>{credit || ''}</td>
                      </tr>
                    );
                  })}

                  {/* Profit and Loss Calculation */}
                  <tr>
                    <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', backgroundColor:'#D3D3D3' }}>
                      {language === "English" ? " Profit " : "नफा"}
                    </td>
                    <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', backgroundColor:'#D3D3D3' }}>
                      {profitValue.toFixed(2)}
                    </td>
                    <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', backgroundColor:'#D3D3D3' }}>
                      {language === "English" ? " Loss " : "तोटा"}
                    </td>
                    <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', backgroundColor:'#D3D3D3' }}>
                      {lossValue.toFixed(2)}
                    </td>
                  </tr>

                  {/* Total Calculation */}
                  <tr>
                    <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                      {language === "English" ? " Total " : "एकूण"}
                    </td>
                    <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                      {(totalDebit + profitValue).toFixed(2)}
                    </td>
                    <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                      {language === "English" ? " Total " : "एकूण"}
                    </td>
                    <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                      {(totalCredit + lossValue).toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const report2Overlay = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backdropFilter: 'blur(3px)',
  zIndex: 1000,
};

const report2Container = {
  background: 'white',
  padding: '20px',
  borderRadius: '8px',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
  maxWidth: '900px',
  width: '100%',
  position: 'relative',
  color:'black',
  overflowY: 'hidden',
  maxHeight: '90vh',
};

const closeButtonStyle2 = {
  position: 'absolute',
  top: '10px',
  right: '10px',
  background: 'transparent',
  border: 'none',
  fontSize: '20px',
  cursor: 'pointer',
  color:'Red'
};

export default ProfitLossReport;